/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../../Core/Components/Modal/CustomModal";
import ChargerIcon from "../../../Common/Svgs/ChargerIcon";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import PlusIcon from "../../../Common/Svgs/PlusIcon";
import Button from "../../../Core/Components/CustomButton";
import { useHubs } from "../useLendEnergyData";
import SelectionDropdown from "../SelectionDropdown";
import TrashIcon from "../../../Common/Svgs/TrashIcon";
import ExistingBatteries from "./ExistingBatteries";
import ColumnHeader from "./ColumnHeader";
import EnergyValue from "./EnergyValue";
import { updateRental } from "../../../Redux/Slices/rentals.slice";
import { client } from "../../../Utils/axiosClient";
import { getRentalList } from "../../../Redux/APIs/rentalsAPI";
import capitalize from "../../../Utils/capitalize";

const AddBatteryEnergyModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { currentRental } = useSelector((state) => state.rentals);
  // const { allBatteries } = useSelector((state) => state.batteries);

  const [availableBatteries, setAvailableBatteries] = useState([]);
  const {
    handleSubmit,
    control,
    formState,
    setValue,
    clearErrors,
    resetField,
    watch,
  } = useForm({
    defaultValues: {
      lendEnergy: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "lendEnergy",
  });

  // list of hubs
  const hubList = useHubs();

  const [existing, setExisting] = useState([]);
  const [lendEnergy, setLendEnergy] = useState(false);
  // start energy (from iot) of the selected lend batteries -> { [batteryId]: { energy, loading } }
  const [lendEnergies, setLendEnergies] = useState({});

  // updating hub with a default value
  const [selectedHub, setSelectedHub] = useState(
    hubList?.length ? hubList[0] : {},
  );

  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const getAvailableBatteries = useCallback(async () => {
    if (visible) {
      try {
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/battery/get-batteries-for-assigning`,
          {
            params: {
              supplierHubId: selectedHub?.id,
            },
          },
        );

        console.log(response.data);
        setAvailableBatteries([...response.data.batteries]);
      } catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedHub]);

  useEffect(() => {
    if (selectedHub && selectedHub.id) {
      getAvailableBatteries();
    }
  }, [getAvailableBatteries, selectedHub, visible]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (selectedHub?.id) {
        try {
          const res = await client.get(
            `${process.env.REACT_APP_API_URL}/hub-station/hub-agents`,
            {
              params: {
                hubId: selectedHub.id,
              },
            },
          );
          const mappedAgents =
            res.data?.map((agent) => ({
              id: agent.id,
              name: agent.name,
              value: agent.name,
            })) || [];
          setAgents(mappedAgents);
          if (mappedAgents.length > 0) {
            setSelectedAgent(mappedAgents[0]);
          } else {
            setSelectedAgent(null);
          }
        } catch (error) {
          console.log("Failed to fetch hub agents:", error);
          setAgents([]);
          setSelectedAgent(null);
        }
      }
    };

    fetchAgents();
  }, [selectedHub?.id]);

  const getBatteries = () => {
    return availableBatteries?.map((item) => ({
      ...item,
      value: item.registrationNo,
    }));
  };

  // clear all states and close the modal
  const handleCancel = () => {
    setExisting([]);
    setLendEnergies({});
    setLendEnergy(false);
    resetField("lendEnergy");
    onCancel();
  };

  // add new line item in the lend energy part
  const handleAppend = () => {
    setLendEnergy(true);

    append({
      id: null,
      registrationNo: "",
    });
  };

  // start energy of the selected battery from iot (display only, recalculated by the api on save)
  const getLendBatteryEnergy = async (batteryId) => {
    setLendEnergies((prev) => ({
      ...prev,
      [batteryId]: { energy: prev[batteryId]?.energy, loading: true },
    }));

    let energy = null;

    try {
      const response = await client.get(
        `${process.env.REACT_APP_API_URL}/rentals/battery-iot-energy`,
        { params: { batteryId, energyType: "start" } },
      );
      energy = response?.data?.energy ?? null;
    } catch (error) {
      console.log(error);
    }

    setLendEnergies((prev) => ({
      ...prev,
      [batteryId]: { energy, loading: false },
    }));
  };

  // on submit
  const onSubmit = async (e) => {
    const payload = {
      rentalId: currentRental.id,
      lendedFromHubId: selectedHub?.id,
      agentId: selectedAgent?.id || null,
      // energies are calculated by the api from iot, the batteries with the driver
      // are taken from the db
      lendedBatteries: e?.lendEnergy?.map((item) => ({
        id: item.id,
        registrationNo: item.registrationNo,
      })),
    };

    try {
      dispatch(updateRental({ key: "loading", value: true }));

      await client.post(
        `${process.env.REACT_APP_API_URL}/rentals/lend-batteries`,
        payload,
      );

      await dispatch(getRentalList());

      dispatch(
        updateRental({
          key: "showToastMessage",
          value: {
            visible: true,
            data: {
              title: `${payload?.lendedBatteries?.length} Batteries Added`,
              description: `Batteries from ${capitalize(
                selectedHub?.name,
              )} was added successfully`,
            },
          },
        }),
      );

      handleCancel();
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(updateRental({ key: "loading", value: false }));
    }
  };

  return (
    <CustomModal visible={visible} onCancel={handleCancel} width="1121px">
      <div
        className="py-4"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="px-4 flex items-center justify-between">
          <div className="flex items-center gap-x-3">
            <div className="flex items-center justify-center w-12 h-12 bg-green rounded-full">
              <ChargerIcon size={24} className="text-yellow" />
            </div>

            <p className="font-poppins text-base font-medium">
              Add Battery Energy
            </p>
          </div>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div className="max-h-[450px] overflow-y-scroll">
          <ExistingBatteries batteries={existing} setBatteries={setExisting} />

          {lendEnergy ? (
            <div className="mt-6">
              <div className="flex items-center justify-between px-4 mb-6 gap-x-4">
                <div className="flex-1">
                  <p className="font-poppins font-medium text-xs">
                    Lend More Energy
                  </p>

                  <p className="mt-1 font-aileron font-normal text-xs text-dark-gray">
                    Select batteries to swap from your hub to meet the energy
                    demand for completing this trip.
                  </p>
                </div>

                <div className="flex items-center gap-x-3">
                  <SelectionDropdown
                    type="hub"
                    data={hubList}
                    placeholder="Select Hub Station"
                    value={selectedHub?.name || ""}
                    onSelect={(data) => setSelectedHub(data)}
                    searchKey="name"
                  />

                  <SelectionDropdown
                    type="hub"
                    data={agents}
                    placeholder="Select Technical Agent"
                    value={selectedAgent?.name || ""}
                    onSelect={(data) => setSelectedAgent(data)}
                    searchKey="name"
                  />
                </div>
              </div>

              <ColumnHeader type="lend" />

              {fields.map((item, index) => {
                return (
                  <div
                    key={item?.id}
                    className="flex items-center py-2 h-12 border-b-[0.5px] border-light-grey"
                  >
                    <div className="flex-1 px-6">
                      <Controller
                        name={`lendEnergy.${index}.registrationNo`}
                        rules={{ required: "required." }}
                        control={control}
                        render={({ field }) => {
                          // check if error exists
                          const error =
                            formState?.errors?.lendEnergy?.[index]
                              ?.registrationNo;

                          return (
                            <SelectionDropdown
                              {...field}
                              placeholder="Select Battery ID"
                              formState={formState}
                              searchKey="registrationNo"
                              data={getBatteries()}
                              error={error}
                              onSelect={(data) => {
                                setValue(`lendEnergy.${index}.id`, data.id);

                                setValue(
                                  `lendEnergy.${index}.registrationNo`,
                                  data.registrationNo,
                                );

                                getLendBatteryEnergy(data.id);

                                clearErrors(
                                  `lendEnergy.${index}.registrationNo`,
                                );
                              }}
                            />
                          );
                        }}
                      />
                    </div>

                    <div className="flex-1 px-6 flex items-center">
                      <EnergyValue
                        value={
                          lendEnergies[watch(`lendEnergy.${index}.id`)]?.energy
                        }
                        loading={
                          lendEnergies[watch(`lendEnergy.${index}.id`)]?.loading
                        }
                      />
                    </div>

                    {fields.length > 1 && (
                      <button
                        type="button"
                        className="absolute flex items-center justify-center w-6 h-6 bg-alert-10 rounded-md right-4"
                        onClick={() => remove(index)}
                      >
                        <TrashIcon className="text-alert-red" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="px-4 mt-6 flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-x-2 py-1.5 px-2"
            onClick={handleAppend}
          >
            <PlusIcon className="w-4 h-4 stroke-green" />
            <p className="font-poppins text-xs font-medium text-green">
              Lend More Energy
            </p>
          </button>

          <div className="flex items-center gap-x-2">
            <Button
              size="filter"
              width="dynamic"
              colors="grey"
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="dynamic"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddBatteryEnergyModal;
